import { useDebugValue, useMemo } from 'react'
import { useQueryClient, UseQueryOptions } from '@tanstack/react-query'
import type {
  GoalsQuery,
  UpdateGoalMutation,
  UpdateGoalMutationVariables,
} from '@purposity/sdk'
import {
  ApiGoal,
  Goal,
  GoalPeriod,
  NeedsMetStats,
} from '@features/goals/helpers'
import { useProfileQuery } from '@features/profile/hooks/useProfile'
import { userQK } from '@features/profile/model/userQK'
import { useMutationWithAuth, useQueryWithAuth } from '@lib/Auth/clerk-utils'
import type { MutationContext } from '@lib/react-query'
//import { analytics } from '@services/Analytics'
import { sdk } from '@services/sdk'
import type { GraphQLError } from 'graphql'
import type { ClientError } from 'graphql-request'

function useGoalQuery<TData = GoalsQuery>(
  options: UseQueryOptions<GoalsQuery, GraphQLError, TData>
) {
  return useQueryWithAuth({
    queryKey: userQK.goals(),
    async queryFn(context) {
      return sdk.goals(
        { cacheRefresh: true },
        context.meta?.headers,
        context.signal
      )
    },
    ...options,
  })
}

export function useGoal() {
  const profileQuery = useProfileQuery({
    select: (data) => {
      const safeData = NeedsMetStats.safeParse(data.me?.needs_met_stats)
      if (!safeData.success) return undefined
      return safeData.data
    },
  })

  const goalQuery = useGoalQuery({
    enabled: !!profileQuery.data,
    select: (data) => {
      const safeData = ApiGoal.safeParse(data.me?.goal)
      if (!safeData.success) return undefined
      return safeData.data
    },
  })

  const finalGoal = useMemo(() => {
    return (
      (profileQuery.data &&
        goalQuery.data &&
        new Goal(goalQuery.data, profileQuery.data)) ||
      null
    )
  }, [profileQuery.data, goalQuery.data])

  useDebugValue({
    gqData: goalQuery.data,
    pqData: profileQuery.data,
    goal: finalGoal,
  })

  return {
    _profileQuery: profileQuery,
    _goalQuery: goalQuery,
    goal: finalGoal,
  }
}

export function useGoalValue() {
  const { goal } = useGoal()
  useDebugValue(goal)

  return goal
}

export function useSetGoal() {
  const queryClient = useQueryClient()
  return useMutationWithAuth<
    UpdateGoalMutation,
    ClientError,
    UpdateGoalMutationVariables,
    MutationContext<GoalsQuery>
  >({
    mutationKey: userQK.goals(),
    async mutationFn(variables) {
      return sdk.updateGoal(variables, this.meta?.headers)
    },

    async onMutate(variables) {
      await queryClient.cancelQueries(userQK.goals())

      const snapshot = queryClient.getQueryData<GoalsQuery>(userQK.goals())

      queryClient.setQueryData<GoalsQuery>(userQK.goals(), (prevData) => {
        const goal = prevData?.me?.goal
        return {
          me: {
            goal: {
              id: goal?.id,
              created_at: goal?.created_at,
              updated_at: new Date().toJSON(),
              goal_period: new GoalPeriod(variables.regularity).noun,
              target_value: Number(variables.number),
            },
          },
        }
      })

      return {
        snapshot,
      }
    },

    onError(_error, _variables, context) {
      const snapshot = context?.snapshot
      if (snapshot) {
        queryClient.setQueryData(userQK.goals(), snapshot)
      }
    },

    onSuccess(_, variables) {
      //TODO: @native: figure out why this calls stripe
      // analytics.track('goal_set', {
      //   target_value: variables.number,
      //   goal_period: new GoalPeriod(variables.regularity).noun,
      // })
    },

    onSettled() {
      return queryClient.invalidateQueries(userQK.goals())
    },
  })
}
